import QRCode from 'react-qr-code';
import { Grid, Stack } from '@mui/material';
import type { ApiProfileOut3 } from '@agunity/api-v4';

import { AgColor } from '../../../stories/Styles/AgColor';
import AgTypography from '../../../stories/Styles/AgTypography';
import { ReactComponent as V3Logo } from '../../../assets/V3Logo.svg';

import {
  addThreeYearsFromToday,
  formatDate,
  removeFalsyProperties,
} from '../../../utils';
import { encrypt, getHash } from '../../../utils/crypto';
import type { QRCodeData, QrUserOrgData } from './types';
import { tokenKey } from '../../../api/environments';
import * as S from './styled';

const IDCard = ({ details }: { details: ApiProfileOut3 }) => {
  const account = details.account;
  const org = details.organizations?.[0];
  const profile = details.profiles?.[0];

  const orgRelation = org?.relationships?.[0];

  const encode: QRCodeData = {
    I: account?.id!,
    U: {
      UserId: profile?.id!,
      FirstName: account?.firstName || '',
      MiddleName: account?.middleName || '',
      FamilyName: account?.familyName || '',
      OtherNames: account?.otherNames || '',
      DOB: account?.dateOfBirth?.split('T')[0] || '',
      Gender: account?.gender || '',
      Street: account?.address?.addressLine1 || '',
      Locality: account?.address?.addressLine2 || '',
      City: account?.address?.city || '',
      Province: account?.address?.province || '',
      CountryId: Number(account?.address?.countryId || ''),
      PhotoId: (account?.photoId || '').toString(),
    },
    O: {
      UserId: profile?.id!,
      OrgId: org?.id!,
      OrgName: org?.name || '',
      FirstName: account?.firstName || '',
      FamilyName: account?.familyName || '',
      Signature: profile?.signature || '',
    } as QrUserOrgData,
  };

  const expire = addThreeYearsFromToday();

  const data = {
    encode: {
      d: encode,
      s: encrypt(expire.toString(), tokenKey),
      h: getHash(encode),
      t: 'IDCard',
    },
    format: 11, // QR Code format
    asBase64: true,
    compress: true,
  };

  const cleanedData = removeFalsyProperties(data);

  return (
    <S.IDCard>
      <Grid container height='100%'>
        <Grid item xs={5} p={1.5} pb={1} pr={0}>
          <Stack justifyContent='space-between' height='100%'>
            <Stack gap={1}>
              <AgTypography
                contentStyle='AP.H2.Bold'
                message={account?.firstName || ''}
                style={{ color: AgColor.qrMain }}
              />
              <AgTypography
                contentStyle='AP.H4.Bold'
                message={account?.familyName || ''}
                style={{ color: AgColor.qrMain }}
              />
              <AgTypography
                contentStyle='AP.H4.Medium'
                message={account?.address?.city || ''}
                style={{ color: AgColor.qrLight }}
              />
            </Stack>
            <Stack
              direction='row'
              alignItems='end'
              justifyContent='space-between'
            >
              <Stack gap={0.5}>
                <AgTypography
                  contentStyle='AP.H4.Medium'
                  message={orgRelation?.orgBName || ''}
                  style={{ color: AgColor.qrMain }}
                />
                <AgTypography
                  contentStyle='AP.H5.Medium'
                  message={`EXP ${formatDate(expire)}`}
                  style={{ color: AgColor.qrLight }}
                />
              </Stack>
              <V3Logo width={60} height={60} />
            </Stack>
          </Stack>
        </Grid>
        <Grid
          item
          xs
          p={1}
          sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}
        >
          <QRCode value={JSON.stringify(cleanedData)} size={215} />
        </Grid>
      </Grid>
    </S.IDCard>
  );
};

export default IDCard;
